import { useMemo, useState } from 'react'
import { db, batchTransact, transactWithRetry, SYSTEM_TEMPLATE_ORG_ID } from '@/lib/db'
import { id } from '@instantdb/react'
import type { NoteImageSlot, NoteStyle, NoteTemplate, CopywritingStyle } from '@/types'
import type { ReceiptType } from '@/config/receipts'
import { defaultNoteStyles, LEGACY_STYLE_MAPPING, type NoteStyleSeed } from '@/config/noteStyles'
import { useEffect, useRef } from 'react'
import { useProfile } from './useProfile'

interface UseNoteStylesResult {
  styles: NoteStyle[]
  templates: NoteTemplate[]
  imageSlots: NoteImageSlot[]
  copywritingStyles: CopywritingStyle[]
  styleMap: Record<string, NoteStyle>
  templateMap: Record<string, NoteTemplate[]>
  templateById: Record<string, NoteTemplate>
  slotMap: Record<string, NoteImageSlot[]>
  copywritingStyleMap: Record<string, CopywritingStyle[]>  // 按 style_id 分组
  copywritingStyleById: Record<string, CopywritingStyle>
  isLoading: boolean
  error: unknown
  isSeeding: boolean
  seedDefaults: () => Promise<void>
  getLegacyMapping: (type: ReceiptType) => { styleId: string; templateId: string } | undefined
  updateStyle: (
    styleId: string,
    updates: Partial<Omit<NoteStyle, 'id' | 'created_at'>>
  ) => Promise<void>
  deleteStyle: (styleId: string) => Promise<void>
  createCopywritingStyle: (style: Omit<CopywritingStyle, 'created_at'>) => Promise<void>
  updateCopywritingStyle: (
    styleId: string,
    updates: Partial<Omit<CopywritingStyle, 'id' | 'style_id' | 'created_at'>>
  ) => Promise<void>
  deleteCopywritingStyle: (styleId: string) => Promise<void>
  cloneStyle: (styleId: string, targetOrgId: string) => Promise<void>
}

function buildTemplateMap(templates: NoteTemplate[]) {
  return templates.reduce<Record<string, NoteTemplate[]>>((acc, tpl) => {
    if (!acc[tpl.style_id]) acc[tpl.style_id] = []
    acc[tpl.style_id].push(tpl)
    return acc
  }, {})
}

function buildSlotMap(slots: NoteImageSlot[]) {
  return slots.reduce<Record<string, NoteImageSlot[]>>((acc, slot) => {
    if (!acc[slot.template_id]) acc[slot.template_id] = []
    acc[slot.template_id].push(slot)
    return acc
  }, {})
}

function buildCopywritingStyleMap(styles: CopywritingStyle[]) {
  return styles.reduce<Record<string, CopywritingStyle[]>>((acc, style) => {
    if (!acc[style.style_id]) acc[style.style_id] = []
    acc[style.style_id].push(style)
    return acc
  }, {})
}

function ensureSeedTxns(
  seeds: NoteStyleSeed[],
  styles: NoteStyle[],
  templates: NoteTemplate[],
  slots: NoteImageSlot[],
  copywritingStyles: CopywritingStyle[],
  orgId?: string
) {
  const txns: unknown[] = []

  const styleIds = new Set(styles.map(s => s.id))
  const templateIds = new Set(templates.map(t => t.id))
  const slotIds = new Set(slots.map(s => s.id))
  const copywritingStyleIds = new Set(copywritingStyles.map(s => s.id))

  seeds.forEach(seed => {
    if (!styleIds.has(seed.style.id)) {
      txns.push(db.tx.note_styles[seed.style.id].update({ ...seed.style, org_id: orgId }))
    }
    // 文案风格
    seed.copywritingStyles.forEach(cpy => {
      if (!copywritingStyleIds.has(cpy.id)) {
        txns.push(db.tx.copywriting_styles[cpy.id].update({ ...cpy, org_id: orgId }))
      }
    })
    // 模板和槽位
    seed.templates.forEach(t => {
      if (!templateIds.has(t.template.id)) {
        txns.push(db.tx.note_templates[t.template.id].update({ ...t.template, org_id: orgId }))
      }
      t.slots.forEach(slot => {
        if (!slotIds.has(slot.id)) {
          txns.push(db.tx.note_image_slots[slot.id].update({ ...slot, org_id: orgId }))
        }
      })
    })
  })

  return txns
}

export function useNoteStyles(overrideOrgId?: string): UseNoteStylesResult {
  const { profile } = useProfile()
  const orgId = overrideOrgId || profile?.org_id

  const queryCondition = orgId
    ? {
        note_styles: { $: { where: { org_id: orgId } } },
        note_templates: { $: { where: { org_id: orgId } } },
        note_image_slots: { $: { where: { org_id: orgId } } },
        copywriting_styles: { $: { where: { org_id: orgId } } },
      }
    : {
        note_styles: {},
        note_templates: {},
        note_image_slots: {},
        copywriting_styles: {},
      }

  const { data, isLoading, error } = db.useQuery(queryCondition)

  const styles = (data?.note_styles || []) as NoteStyle[]
  const templates = (data?.note_templates || []) as NoteTemplate[]
  const imageSlots = (data?.note_image_slots || []) as NoteImageSlot[]
  const copywritingStyles = (data?.copywriting_styles || []) as CopywritingStyle[]

  const styleMap = useMemo(() => {
    return styles.reduce<Record<string, NoteStyle>>((acc, style) => {
      acc[style.id] = style
      return acc
    }, {})
  }, [styles])

  const templateById = useMemo(() => {
    return templates.reduce<Record<string, NoteTemplate>>((acc, tpl) => {
      acc[tpl.id] = tpl
      return acc
    }, {})
  }, [templates])

  const copywritingStyleById = useMemo(() => {
    return copywritingStyles.reduce<Record<string, CopywritingStyle>>((acc, style) => {
      acc[style.id] = style
      return acc
    }, {})
  }, [copywritingStyles])

  const templateMap = useMemo(() => buildTemplateMap(templates), [templates])
  const slotMap = useMemo(() => buildSlotMap(imageSlots), [imageSlots])
  const copywritingStyleMap = useMemo(() => buildCopywritingStyleMap(copywritingStyles), [copywritingStyles])

  const [isSeeding, setIsSeeding] = useState(false)
  const autoSeedAttempted = useRef(false)

  const seedDefaults = async () => {
    const txns = ensureSeedTxns(defaultNoteStyles, styles, templates, imageSlots, copywritingStyles, orgId)
    if (!txns.length) return
    setIsSeeding(true)
    try {
      // 使用分批处理种子数据
      await batchTransact(txns)
    } finally {
      setIsSeeding(false)
    }
  }

  const updateStyle = async (
    styleId: string,
    updates: Partial<Omit<NoteStyle, 'id' | 'created_at'>>
  ) => {
    await transactWithRetry(db.tx.note_styles[styleId].update({ ...updates, org_id: orgId }))
  }

  // 删除笔记风格（级联删除所有模板和槽位）
  const deleteStyle = async (styleId: string) => {
    // 1. 查询该风格下的所有模板和槽位
    const templatesUnderStyle = templates.filter(t => t.style_id === styleId)
    const templateIds = templatesUnderStyle.map(t => t.id)
    const slotsUnderTemplates = imageSlots.filter(s => templateIds.includes(s.template_id))

    // 2. 构建删除事务（级联删除：风格 + 模板 + 槽位）
    const txns = [
      db.tx.note_styles[styleId].delete(),
      ...templatesUnderStyle.map(t => db.tx.note_templates[t.id].delete()),
      ...slotsUnderTemplates.map(s => db.tx.note_image_slots[s.id].delete()),
    ]

    // 3. 使用分批删除（避免超时）
    await batchTransact(txns)
  }

  const getLegacyMapping = (type: ReceiptType) => LEGACY_STYLE_MAPPING[type]

  // 文案风格 CRUD
  const createCopywritingStyle = async (style: Omit<CopywritingStyle, 'created_at'>) => {
    await transactWithRetry(
      db.tx.copywriting_styles[style.id].update({
        ...style,
        org_id: orgId,
        created_at: Date.now(),
      })
    )
  }

  const updateCopywritingStyle = async (
    styleId: string,
    updates: Partial<Omit<CopywritingStyle, 'id' | 'style_id' | 'created_at'>>
  ) => {
    await transactWithRetry(db.tx.copywriting_styles[styleId].update(updates))
  }

  const deleteCopywritingStyle = async (styleId: string) => {
    await transactWithRetry(db.tx.copywriting_styles[styleId].delete())
  }

  // 克隆风格到目标企业
  const cloneStyle = async (styleId: string, targetOrgId: string) => {
    const style = styleMap[styleId]
    if (!style) return

    const txns: any[] = []
    const newStyleId = id()
    const templateIdMap: Record<string, string> = {}

    // 1. 克隆风格主体
    txns.push(db.tx.note_styles[newStyleId].update({
      ...style,
      id: newStyleId,
      org_id: targetOrgId,
      created_at: Date.now(),
    }))

    // 2. 克隆文案风格
    const styleCopywritingStyles = copywritingStyleMap[styleId] || []
    styleCopywritingStyles.forEach(cpy => {
      const newCpyId = id()
      txns.push(db.tx.copywriting_styles[newCpyId].update({
        ...cpy,
        id: newCpyId,
        style_id: newStyleId,
        org_id: targetOrgId,
        created_at: Date.now(),
      }))
    })

    // 3. 克隆模板
    const styleTemplates = templateMap[styleId] || []
    styleTemplates.forEach(tpl => {
      const newTplId = id()
      templateIdMap[tpl.id] = newTplId
      txns.push(db.tx.note_templates[newTplId].update({
        ...tpl,
        id: newTplId,
        style_id: newStyleId,
        org_id: targetOrgId,
        created_at: Date.now(),
      }))
    })

    // 4. 克隆槽位
    const styleTemplateIds = styleTemplates.map(t => t.id)
    const styleSlots = imageSlots.filter(s => styleTemplateIds.includes(s.template_id))
    styleSlots.forEach(slot => {
      const newSlotId = id()
      const newTplId = templateIdMap[slot.template_id]
      if (newTplId) {
        txns.push(db.tx.note_image_slots[newSlotId].update({
          ...slot,
          id: newSlotId,
          template_id: newTplId,
          org_id: targetOrgId,
          created_at: Date.now(),
        }))
      }
    })

    await batchTransact(txns)
  }

  // 自动预置默认风格（仅在系统模板企业下、且数据为空时执行）
  useEffect(() => {
    if (autoSeedAttempted.current) return
    if (isLoading || isSeeding) return
    if (!orgId || orgId !== SYSTEM_TEMPLATE_ORG_ID) return // 仅限系统模板企业自动预置
    if (styles.length > 0) return
    autoSeedAttempted.current = true
    seedDefaults().catch((err) => {
      // 失败时仅提示一次，不影响后续手动写入
      console.warn('默认风格写入失败，可在笔记风格页手动写入', err)
    })
  }, [isLoading, isSeeding, styles.length, orgId])

  return {
    styles,
    templates,
    imageSlots,
    copywritingStyles,
    styleMap,
    templateMap,
    templateById,
    slotMap,
    copywritingStyleMap,
    copywritingStyleById,
    isLoading,
    error,
    isSeeding,
    seedDefaults,
    getLegacyMapping,
    updateStyle,
    deleteStyle,
    createCopywritingStyle,
    updateCopywritingStyle,
    deleteCopywritingStyle,
    cloneStyle,
  }
}

